<template>
  <div class="ka-price-chart" :class="{ 'ka-pc--mobile': isMobile }">

    <!-- Mobile: Spot prices -->
    <spot-price-bar
      v-if="isMobile && showSpot"
      :items="spotPriceData"
      :unit-suffix="activeUnit.suffix"
    />

    <h2 class="ka-pc-title">{{ t.title }}</h2>

    <!-- Desktop: Full filter bar -->
    <filter-bar
      v-if="!isMobile"
      :t="t"
      :metals="metals"
      :units="units"
      :currency-options="currencyOptions"
      :selected-metals="selectedMetals"
      :selected-unit="selectedUnit"
      :selected-currency="selectedCurrency"
      :date-from="dateFrom"
      :date-to="dateTo"
      @metal="setMetal"
      @unit="setUnit"
      @currency="setCurrency"
      @update:dateFrom="dateFrom = $event; fetchData()"
      @update:dateTo="dateTo = $event; fetchData()"
    />

    <!-- Mobile: Compact filter row -->
    <div v-if="isMobile" class="ka-pc-mobile-header">
      <button class="ka-pc-btn ka-pc-filter-btn" @click="filterModalOpen = true">
        {{ t.filterButton }}
      </button>
    </div>

    <div class="ka-pc-main">
      <div class="ka-pc-chart-col">
        <div class="ka-pc-chart-container">
          <div v-if="loading" class="ka-pc-overlay">
            <span>{{ t.loadingText }}</span>
          </div>
          <div v-if="error && !loading" class="ka-pc-overlay ka-pc-error">
            <span>{{ t.errorText }}</span>
          </div>
          <area-chart
            v-if="activeDisplayData.labels.length > 0"
            :chart-data="activeDisplayData"
            :options="activeOptions"
            :animate="!sliding"
            :style="{ height: isMobile ? '260px' : '400px', position: 'relative' }"
          />
          <chart-tooltip
            ref="ttEl"
            :visible="tt.visible"
            :date="tt.date"
            :rows="tt.rows"
            :left="tt.left"
            :top="tt.top"
            :docked="tt.docked"
          />
          <div
            v-if="!loading && !error && activeDisplayData.labels.length === 0"
            class="ka-pc-no-data"
          >{{ t.noDataText }}</div>
        </div>

        <!-- Desktop: Time spans + slider -->
        <time-span-bar
          v-if="!isMobile"
          :t="t"
          :time-spans="timeSpans"
          :active-time-span="activeTimeSpan"
          :slider-start="sliderStart"
          :slider-end="sliderEnd"
          :max-index="sliderMaxIndex"
          @timespan="setTimeSpan"
          @update:sliderStart="onSliderInput('start', $event)"
          @update:sliderEnd="onSliderInput('end', $event)"
          @slidestart="sliding = true"
          @slideend="sliding = false"
        />

        <!-- Mobile: Compact time spans (no slider) -->
        <div v-if="isMobile" class="ka-pc-time-spans">
          <button
            v-for="ts in mobileTimeSpans"
            :key="ts.key"
            :class="['ka-pc-ts-btn', { active: activeTimeSpan === ts.key }]"
            @click="setTimeSpan(ts.key)"
          >{{ ts.label }}</button>
        </div>
      </div>

      <!-- Desktop: Sidebar -->
      <div v-if="!isMobile" class="ka-pc-sidebar">
        <key-figures-card v-if="!isMultiMetal" :t="t" :figures="keyFigures" />
        <comparison-card :t="t" :items="comparisonData" />
      </div>
    </div>

    <!-- Mobile: Key figures below chart -->
    <key-figures-card
      v-if="isMobile && showKeyFigures && !isMultiMetal"
      :t="t"
      :figures="keyFigures"
    />

    <!-- Mobile: Filter modal -->
    <filter-modal
      v-if="filterModalOpen"
      :t="t"
      :metals="metals"
      :units="units"
      :currency-options="currencyOptions"
      :selected-metals="selectedMetals"
      :selected-unit="selectedUnit"
      :selected-currency="selectedCurrency"
      :date-from="dateFrom"
      :date-to="dateTo"
      @close="filterModalOpen = false"
      @apply="applyFilter"
    />
  </div>
</template>

<script>
import AreaChart from './components/AreaChart.js'
import FilterBar from './components/FilterBar.vue'
import TimeSpanBar from './components/TimeSpanBar.vue'
import KeyFiguresCard from './components/KeyFiguresCard.vue'
import ComparisonCard from './components/ComparisonCard.vue'
import FilterModal from './components/FilterModal.vue'
import SpotPriceBar from './components/SpotPriceBar.vue'
import ChartTooltip from './components/ChartTooltip.vue'

import { DEFAULT_TEXTS, TIME_SPAN_OFFSETS, MOBILE_TIME_SPAN_KEYS, METAL_COLORS } from './utils/constants'
import { MONTHS_NO, toDateString, formatNumber, formatPercent, priceForUnit, percentChange } from './utils/format'
import { buildApiUrl, isValidApiResponse, parseApiResponse } from './utils/api'

export default {
  name: 'PriceChart',
  components: { AreaChart, FilterBar, TimeSpanBar, KeyFiguresCard, ComparisonCard, FilterModal, SpotPriceBar, ChartTooltip },
  props: {
    texts: { type: Object, default: () => ({}) },
    apiUrl: { type: String, default: '/wp-json/price-history/v1/prices' },
    defaultMetal: { type: String, default: 'AU' },
    defaultUnit: { type: String, default: 'gram' },
    defaultCurrency: { type: String, default: 'nok' },
    mobileMode: { type: String, default: 'full' }
  },

  data() {
    const now = new Date();
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(now.getMonth() - 3);

    return {
      selectedMetals: [this.defaultMetal],
      selectedUnit: this.defaultUnit,
      selectedCurrency: this.defaultCurrency,
      dateFrom: toDateString(threeMonthsAgo),
      dateTo: toDateString(now),
      allData: [],
      sidebarData: [],
      loading: false,
      error: false,
      activeTimeSpan: '3m',
      sliderStart: 0,
      sliderEnd: 0,
      sliding: false,
      fetchController: null,
      // Mobile state
      isMobile: false,
      filterModalOpen: false,
      mobileMediaQuery: null,
      // Tooltip state (driven by Chart.js external callback)
      tt: { visible: false, date: '', rows: [], left: 0, top: 0, docked: false }
    }
  },

  computed: {
    t() {
      return { ...DEFAULT_TEXTS, ...this.texts };
    },

    metals() {
      return [
        { code: 'AU', label: this.t.metalGold },
        { code: 'AG', label: this.t.metalSilver },
        { code: 'PD', label: this.t.metalPalladium },
        { code: 'PT', label: this.t.metalPlatinum }
      ];
    },

    units() {
      return [
        { key: 'ounce', label: this.t.unitOunce, suffix: '/oz' },
        { key: 'gram', label: this.t.unitGram, suffix: '/g' },
        { key: 'kg', label: this.t.unitKg, suffix: '/kg' }
      ];
    },

    currencyOptions() {
      return ['NOK', 'SEK', 'EUR', 'USD'];
    },

    isMultiMetal() {
      return this.selectedMetals.length > 1;
    },

    activeUnit() {
      return this.units.find(u => u.key === this.selectedUnit) || this.units[1];
    },

    unitSuffix() {
      return this.selectedCurrency.toUpperCase() + this.activeUnit.suffix;
    },

    currentMetalData() {
      const metalData = this.allData.find(d => d.code === this.selectedMetals[0]);
      if (!metalData)
        return [];
      const currPrices = metalData.prices[this.selectedCurrency];
      if (!currPrices || currPrices.length === 0)
        return [];
      const unit = this.selectedUnit;
      return currPrices.map(p => ({
        dateLabel: p.dateLabel,
        rawDate: p.rawDate,
        price: priceForUnit(p, unit)
      }));
    },

    sliderMaxIndex() {
      if (this.isMultiMetal) {
        return Math.max(0, this.alignedMetalData.dates.length - 1);
      }
      return Math.max(0, this.currentMetalData.length - 1);
    },

    alignedMetalData() {
      var currency = this.selectedCurrency;
      var unit = this.selectedUnit;
      var metalMaps = {};
      var codes = [];

      this.selectedMetals.forEach(function (code) {
        var metalData;
        for (var i = 0; i < this.allData.length; i++) {
          if (this.allData[i].code === code) { metalData = this.allData[i]; break; }
        }
        if (!metalData) return;
        var currPrices = metalData.prices[currency];
        if (!currPrices || currPrices.length === 0) return;

        var priceMap = {};
        currPrices.forEach(function (p) {
          priceMap[p.dateKey] = { dateLabel: p.dateLabel, rawDate: p.rawDate, price: priceForUnit(p, unit) };
        });
        metalMaps[code] = priceMap;
        codes.push(code);
      }.bind(this));

      if (codes.length === 0) return { dates: [], metals: {}, codes: [] };

      // Intersect date keys across all metals
      var firstKeys = Object.keys(metalMaps[codes[0]]);
      var common = {};
      firstKeys.forEach(function (k) { common[k] = true; });
      for (var i = 1; i < codes.length; i++) {
        var map = metalMaps[codes[i]];
        var next = {};
        Object.keys(common).forEach(function (k) {
          if (map[k]) next[k] = true;
        });
        common = next;
      }

      var sortedKeys = Object.keys(common).sort(function (a, b) {
        return metalMaps[codes[0]][a].rawDate - metalMaps[codes[0]][b].rawDate;
      });

      var dates = sortedKeys.map(function (k) {
        var entry = metalMaps[codes[0]][k];
        return { dateLabel: entry.dateLabel, rawDate: entry.rawDate };
      });

      var metals = {};
      codes.forEach(function (code) {
        metals[code] = sortedKeys.map(function (k) { return metalMaps[code][k].price; });
      });

      return { dates: dates, metals: metals, codes: codes };
    },

    visibleData() {
      const data = this.currentMetalData;
      if (data.length === 0)
        return [];
      const start = Math.min(this.sliderStart, data.length - 1);
      const end = Math.min(this.sliderEnd, data.length - 1);
      return data.slice(start, end + 1);
    },

    displayChartData() {
      if (this.isMultiMetal) {
        var aligned = this.alignedMetalData;
        if (aligned.dates.length === 0) return { labels: [], datasets: [] };
        var start = Math.min(this.sliderStart, aligned.dates.length - 1);
        var end = Math.min(this.sliderEnd, aligned.dates.length - 1);
        var labels = aligned.dates.slice(start, end + 1).map(function (d) { return d.dateLabel; });
        var self = this;
        var datasets = aligned.codes.map(function (code) {
          var prices = aligned.metals[code].slice(start, end + 1);
          var base = prices[0];
          var pctData = prices.map(function (p) {
            return base !== 0 ? parseFloat(((p - base) / base * 100).toFixed(2)) : 0;
          });
          var colors = METAL_COLORS[code] || METAL_COLORS.AU;
          var metalDef;
          for (var i = 0; i < self.metals.length; i++) {
            if (self.metals[i].code === code) { metalDef = self.metals[i]; break; }
          }
          return {
            label: metalDef ? metalDef.label : code,
            data: pctData,
            borderColor: colors.border,
            backgroundColor: colors.bg,
            _gradientStops: colors.bgStops,
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 4,
            pointHoverBackgroundColor: colors.border,
            pointHoverBorderColor: '#fff',
            pointHoverBorderWidth: 2,
            fill: false,
            tension: 0.3,
            _rawPrices: prices,
            _metalCode: code
          };
        });
        return { labels: labels, datasets: datasets };
      }

      var data = this.visibleData;
      if (data.length === 0)
        return { labels: [], datasets: [] };
      var singleColors = METAL_COLORS[this.selectedMetals[0]] || METAL_COLORS.AU;
      return {
        labels: data.map(d => d.dateLabel),
        datasets: [{
          label: this.selectedCurrency.toUpperCase(),
          data: data.map(d => parseFloat(d.price.toFixed(2))),
          borderColor: singleColors.border,
          backgroundColor: singleColors.bg,
          _gradientStops: singleColors.bgStops,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 5,
          pointHoverBackgroundColor: singleColors.border,
          pointHoverBorderColor: '#fff',
          pointHoverBorderWidth: 2,
          fill: true,
          tension: 0.3
        }]
      };
    },

    chartOptions() {
      const isMulti = this.isMultiMetal;
      const visibleData = this.visibleData;
      const currency = this.selectedCurrency.toUpperCase();
      const unitSuffix = this.activeUnit.suffix;

      // For multi-metal tooltip: sliced aligned dates
      var alignedDates = [];
      if (isMulti) {
        var ad = this.alignedMetalData;
        var s = Math.min(this.sliderStart, ad.dates.length - 1);
        var e = Math.min(this.sliderEnd, ad.dates.length - 1);
        alignedDates = ad.dates.slice(s, e + 1);
      }

      var vm = this;
      return {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false
        },
        plugins: {
          legend: {
            display: isMulti,
            labels: isMulti ? {
              usePointStyle: true,
              pointStyle: 'circle',
              padding: 12,
              font: { size: 11 }
            } : {}
          },
          tooltip: {
            enabled: false,
            mode: 'index',
            intersect: false,
            external: function(context) {
              var tip = context.tooltip;
              if (tip.opacity === 0) {
                vm.tt = { visible: false, date: '', rows: [], left: 0, top: 0, docked: false };
                return;
              }
              if (!(tip.dataPoints && tip.dataPoints[0])) return;

              var idx = tip.dataPoints[0].dataIndex;
              var dateStr = '';
              var rows = [];

              if (isMulti) {
                var dateEntry = alignedDates[idx];
                if (dateEntry && dateEntry.rawDate) {
                  var dd = dateEntry.rawDate;
                  dateStr = String(dd.getDate()).padStart(2, '0') + '. ' + MONTHS_NO[dd.getMonth()] + ' ' + dd.getFullYear();
                }
                tip.dataPoints.forEach(function(pt) {
                  var ds = context.chart.data.datasets[pt.datasetIndex];
                  var rawPrice = ds._rawPrices ? ds._rawPrices[idx] : 0;
                  var pctVal = pt.parsed.y;
                  rows.push({
                    label: ds.label,
                    color: ds.borderColor,
                    price: formatNumber(rawPrice) + ' ' + currency + unitSuffix,
                    pct: formatPercent(pctVal),
                    pctVal: pctVal
                  });
                });
              } else {
                var pt = tip.dataPoints[0];
                var val = pt.parsed.y;
                var entry = visibleData[idx];
                if (entry && entry.rawDate) {
                  var dd2 = entry.rawDate;
                  dateStr = String(dd2.getDate()).padStart(2, '0') + '. ' + MONTHS_NO[dd2.getMonth()] + ' ' + dd2.getFullYear();
                }
                var prev = idx > 0 ? pt.dataset.data[idx - 1] : val;
                var pct = percentChange(val, prev);
                rows.push({
                  price: formatNumber(val) + ' ' + currency + unitSuffix,
                  pct: formatPercent(pct),
                  pctVal: pct
                });
              }

              var gap = 20;
              var canvas = context.chart.canvas;
              var container = canvas.closest('.ka-pc-chart-container');
              var canvasOffset = container ? (canvas.getBoundingClientRect().left - container.getBoundingClientRect().left) : 0;
              var caretX = tip.caretX + canvasOffset;
              var bw = container ? container.offsetWidth : canvas.parentNode.offsetWidth;
              var ttRef = vm.$refs.ttEl;
              var ew = (ttRef && ttRef.$el) ? ttRef.$el.offsetWidth : 180;
              var left;
              if (caretX + gap + ew + 8 <= bw) {
                left = caretX + gap;
              } else {
                left = caretX - gap - ew;
                if (left < 8) left = 8;
              }
              var top = tip.caretY - ((ttRef && ttRef.$el) ? ttRef.$el.offsetHeight / 2 : 20);

              vm.tt = { visible: true, date: dateStr, rows: rows, left: left, top: top, docked: false };
            }
          }
        },
        scales: {
          x: {
            display: true,
            grid: { display: true, drawOnChartArea: false, drawTicks: true, tickLength: 4, color: '#ccc' },
            border: { display: false },
            ticks: {
              maxTicksLimit: 8,
              color: '#999',
              font: { size: 11 },
              padding: 2
            }
          },
          y: {
            display: true,
            position: 'right',
            grid: {
              borderDash: [3, 3],
              color: '#E5DFD5',
              drawBorder: false
            },
            ticks: {
              color: '#999',
              font: { size: 11 },
              padding: 10,
              callback: isMulti
                ? function (value) { return value.toFixed(1) + '%'; }
                : undefined
            }
          }
        }
      }
    },

    sidebarMetalData() {
      const metalData = this.sidebarData.find(d => d.code === this.selectedMetals[0]);
      if (!metalData)
        return [];
      const currPrices = metalData.prices[this.selectedCurrency];
      if (!currPrices || currPrices.length === 0)
        return [];
      const unit = this.selectedUnit;
      return currPrices.map(p => ({ price: priceForUnit(p, unit) }));
    },

    keyFigures() {
      const data = this.sidebarMetalData;
      const empty = { formatted: '\u2014', change: '', changeClass: '' };
      if (data.length === 0) {
        return { latest: empty, highest: { formatted: '\u2014' }, lowest: empty };
      }

      const latest = data[data.length - 1].price;
      const prev = data.length > 1 ? data[data.length - 2].price : latest;
      const latestPct = percentChange(latest, prev);

      const prices = data.map(d => d.price);
      const highest = Math.max(...prices);
      const lowest = Math.min(...prices);
      const highestPct = latest !== 0 ? ((highest - latest) / latest * 100) : 0;
      const lowestPct = latest !== 0 ? ((lowest - latest) / latest * 100) : 0;

      const result = {
        latest: {
          formatted: this.formatPrice(latest),
          change: this.formatPct(latestPct),
          changeClass: latestPct >= 0 ? 'positive' : 'negative'
        },
        highest: {
          formatted: this.formatPrice(highest),
          change: this.formatPct(highestPct),
          changeClass: highestPct >= 0 ? 'positive' : 'negative'
        },
        lowest: {
          formatted: this.formatPrice(lowest),
          change: this.formatPct(lowestPct),
          changeClass: lowestPct >= 0 ? 'positive' : 'negative'
        }
      }

      // Interval change: absolute currency change + % change from start to end of selected time span (slider).
      const visible = this.visibleData;
      if (!this.isMultiMetal && visible.length >= 2) {
        const first = visible[0].price;
        const last = visible[visible.length - 1].price;
        const diff = last - first;
        const ivPct = percentChange(last, first);
        result.intervalChange = {
          formatted: (diff >= 0 ? '+' : '') + formatNumber(diff) + ' ' + this.unitSuffix,
          change: this.formatPct(ivPct),
          changeClass: ivPct >= 0 ? 'positive' : 'negative'
        };
      }

      return result;
    },

    comparisonData() {
      const unit = this.selectedUnit;
      const currency = this.selectedCurrency;
      return this.metals.map(m => {
        const metalData = this.sidebarData.find(d => d.code === m.code);
        if (!metalData || !metalData.prices[currency] || metalData.prices[currency].length === 0) {
          return { code: m.code, name: m.label, formatted: '\u2014', change: '', changeClass: '' };
        }
        const prices = metalData.prices[currency];
        const latest = priceForUnit(prices[prices.length - 1], unit);
        const prev = priceForUnit(prices.length > 1 ? prices[prices.length - 2] : prices[prices.length - 1], unit);
        const pct = percentChange(latest, prev);
        return {
          code: m.code,
          name: m.label,
          formatted: this.formatPrice(latest),
          change: this.formatPct(pct),
          changeClass: pct >= 0 ? 'positive' : 'negative'
        };
      });
    },

    timeSpans() {
      return [
        { key: '1d', label: this.t.timeSpan1d },
        { key: '1w', label: this.t.timeSpan1w },
        { key: '1m', label: this.t.timeSpan1m },
        { key: '3m', label: this.t.timeSpan3m },
        { key: '6m', label: this.t.timeSpan6m },
        { key: 'ytd', label: this.t.timeSpanYtd },
        { key: '1y', label: this.t.timeSpan1y },
        { key: '3y', label: this.t.timeSpan3y },
        { key: '5y', label: this.t.timeSpan5y },
        { key: '10y', label: this.t.timeSpan10y },
        { key: 'max', label: this.t.timeSpanMax }
      ];
    },

    // Mobile display modes, found in Elementor widget settings. Decides which components to show.

    showSpot() {
      return this.mobileMode === 'full' || this.mobileMode === 'chart-spot';
    },

    showKeyFigures() {
      return this.mobileMode === 'full' || this.mobileMode === 'chart-keyfigures';
    },

    mobileTimeSpans() {
      return this.timeSpans.filter(ts => MOBILE_TIME_SPAN_KEYS.indexOf(ts.key) !== -1);
    },

    // Get data for selected metal on mobile
    mobileMetalPoints() {
      const metalData = this.allData.find(d => d.code === this.selectedMetals[0]);
      if (!metalData)
        return [];
      const currPrices = metalData.prices[this.selectedCurrency];
      if (!currPrices || currPrices.length === 0)
        return [];
      const unit = this.selectedUnit;
      let points = currPrices.map(p => ({
        dateLabel: p.dateLabel,
        rawDate: p.rawDate,
        dateKey: p.dateKey,
        price: priceForUnit(p, unit)
      }));
      return points;
    },

    mobileDisplayChartData() {
      if (this.isMultiMetal) {
        var aligned = this.alignedMetalData;
        if (aligned.dates.length === 0) return { labels: [], datasets: [] };
        var labels = aligned.dates.map(function (d) { return d.dateLabel; });
        var self = this;
        var datasets = aligned.codes.map(function (code) {
          var prices = aligned.metals[code];
          var base = prices[0];
          var pctData = prices.map(function (p) {
            return base !== 0 ? parseFloat(((p - base) / base * 100).toFixed(2)) : 0;
          });
          var colors = METAL_COLORS[code] || METAL_COLORS.AU;
          var metalDef;
          for (var i = 0; i < self.metals.length; i++) {
            if (self.metals[i].code === code) { metalDef = self.metals[i]; break; }
          }
          return {
            label: metalDef ? metalDef.label : code,
            data: pctData,
            borderColor: colors.border,
            backgroundColor: colors.bg,
            _gradientStops: colors.bgStops,
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 4,
            pointHoverBackgroundColor: colors.border,
            pointHoverBorderColor: '#fff',
            pointHoverBorderWidth: 2,
            fill: false,
            tension: 0.3,
            _rawPrices: prices,
            _metalCode: code
          };
        });
        return { labels: labels, datasets: datasets };
      }

      const points = this.mobileMetalPoints;
      if (points.length === 0) return { labels: [], datasets: [] };
      return {
        labels: points.map(p => p.dateLabel),
        datasets: [{
          label: this.selectedCurrency.toUpperCase(),
          data: points.map(p => parseFloat(p.price.toFixed(2))),
          borderColor: (METAL_COLORS[this.selectedMetals[0]] || METAL_COLORS.AU).border,
          backgroundColor: (METAL_COLORS[this.selectedMetals[0]] || METAL_COLORS.AU).bg,
          _gradientStops: (METAL_COLORS[this.selectedMetals[0]] || METAL_COLORS.AU).bgStops,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 5,
          pointHoverBackgroundColor: (METAL_COLORS[this.selectedMetals[0]] || METAL_COLORS.AU).border,
          pointHoverBorderColor: '#fff',
          pointHoverBorderWidth: 2,
          fill: true,
          tension: 0.3
        }]
      };
    },

    mobileChartOptions() {
      const isMulti = this.isMultiMetal;
      const mobilePoints = this.mobileMetalPoints;
      const currency = this.selectedCurrency.toUpperCase();
      const unitSuffix = this.activeUnit.suffix;

      // For multi-metal tooltip on mobile
      var alignedDates = [];
      if (isMulti) {
        alignedDates = this.alignedMetalData.dates;
      }

      var vm = this;
      return {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: {
            display: isMulti,
            labels: isMulti ? {
              usePointStyle: true,
              pointStyle: 'circle',
              padding: 8,
              font: { size: 10 }
            } : {}
          },
          tooltip: {
            enabled: false,
            mode: 'index',
            intersect: false,
            external: function(context) {
              var tip = context.tooltip;
              if (tip.opacity === 0) {
                vm.tt = { visible: false, date: '', rows: [], left: 0, top: 0, docked: false };
                return;
              }
              if (!(tip.dataPoints && tip.dataPoints[0])) return;

              var idx = tip.dataPoints[0].dataIndex;
              var dateStr = '';
              var rows = [];

              if (isMulti) {
                var dateEntry = alignedDates[idx];
                if (dateEntry && dateEntry.rawDate) {
                  var dd = dateEntry.rawDate;
                  dateStr = String(dd.getDate()).padStart(2, '0') + '. ' + MONTHS_NO[dd.getMonth()] + ' ' + dd.getFullYear();
                }
                tip.dataPoints.forEach(function(pt) {
                  var ds = context.chart.data.datasets[pt.datasetIndex];
                  var rawPrice = ds._rawPrices ? ds._rawPrices[idx] : 0;
                  var pctVal = pt.parsed.y;
                  rows.push({
                    label: ds.label,
                    color: ds.borderColor,
                    price: formatNumber(rawPrice) + ' ' + currency + unitSuffix,
                    pct: formatPercent(pctVal),
                    pctVal: pctVal
                  });
                });
              } else {
                var pt = tip.dataPoints[0];
                var val = pt.parsed.y;
                var entry = mobilePoints[idx];
                if (entry && entry.rawDate) {
                  var dd2 = entry.rawDate;
                  dateStr = String(dd2.getDate()).padStart(2, '0') + '. ' + MONTHS_NO[dd2.getMonth()] + ' ' + dd2.getFullYear();
                }
                var prev = idx > 0 ? pt.dataset.data[idx - 1] : val;
                var pct = percentChange(val, prev);
                rows.push({
                  price: formatNumber(val) + ' ' + currency + unitSuffix,
                  pct: formatPercent(pct),
                  pctVal: pct
                });
              }

              var isSmallPhone = window.innerWidth < 467;
              if (isSmallPhone) {
                vm.tt = { visible: true, date: dateStr, rows: rows, left: 0, top: 0, docked: true };
              } else {
                var gap = 25;
                var canvas = context.chart.canvas;
                var container = canvas.closest('.ka-pc-chart-container');
                var canvasOffset = container ? (canvas.getBoundingClientRect().left - container.getBoundingClientRect().left) : 0;
                var caretX = tip.caretX + canvasOffset;
                var bw = container ? container.offsetWidth : canvas.parentNode.offsetWidth;
                var ttRef = vm.$refs.ttEl;
                var ew = (ttRef && ttRef.$el) ? ttRef.$el.offsetWidth : 180;
                var left;
                if (caretX + gap + ew + 8 <= bw) {
                  left = caretX + gap;
                } else {
                  left = caretX - gap - ew;
                  if (left < 8) left = 8;
                }
                var top = tip.caretY - ((ttRef && ttRef.$el) ? ttRef.$el.offsetHeight / 2 : 20);
                vm.tt = { visible: true, date: dateStr, rows: rows, left: left, top: top, docked: false };
              }
            }
          }
        },
        scales: {
          x: {
            display: true,
            grid: { display: true, drawOnChartArea: false, drawTicks: true, tickLength: 4, color: '#ccc' },
            border: { display: false },
            ticks: {
              maxTicksLimit: 5,
              color: '#999',
              font: { size: 10 },
              padding: 2,
              maxRotation: 45,
              minRotation: 45
            }
          },
          y: {
            display: true,
            position: 'right',
            grid: { borderDash: [3, 3], color: '#E5DFD5', drawBorder: false },
            ticks: {
              color: '#999',
              font: { size: 10 },
              padding: 6,
              callback: isMulti
                ? function (value) { return value.toFixed(1) + '%'; }
                : undefined
            }
          }
        }
      };
    },

    spotPriceData() {
      const code = this.selectedMetals[0];
      const metalDef = this.metals.find(m => m.code === code);
      const metalData = this.sidebarData.find(d => d.code === code);
      if (!metalData || !metalData.prices[this.selectedCurrency] || metalData.prices[this.selectedCurrency].length === 0) {
        return [{ code, name: metalDef ? metalDef.label : code, formatted: '\u2014', change: '', changeClass: '' }];
      }
      const prices = metalData.prices[this.selectedCurrency];
      const latest = priceForUnit(prices[prices.length - 1], this.selectedUnit);
      const prev = priceForUnit(prices.length > 1 ? prices[prices.length - 2] : prices[prices.length - 1], this.selectedUnit);
      const pct = percentChange(latest, prev);
      return [{
        code,
        name: metalDef ? metalDef.label : code,
        formatted: formatNumber(latest),
        change: this.formatPct(pct),
        changeClass: pct >= 0 ? 'positive' : 'negative'
      }];
    },

    // Delegates: pick desktop or mobile chart data/options
    activeDisplayData() {
      return this.isMobile ? this.mobileDisplayChartData : this.displayChartData;
    },

    activeOptions() {
      return this.isMobile ? this.mobileChartOptions : this.chartOptions;
    }
  },

  methods: {
    async fetchData() {
      if (this.fetchController)
        this.fetchController.abort();
      this.fetchController = new AbortController();

      this.loading = true;
      this.error = false;
      try {
        const url = buildApiUrl(this.apiUrl, this.dateFrom, this.dateTo);
        const response = await fetch(url, { signal: this.fetchController.signal });
        if (!response.ok)
          throw new Error('API returned ' + response.status);
        const data = await response.json();
        this.allData = isValidApiResponse(data) ? parseApiResponse(data) : [];
        this.resetSlider();
      } catch (e) {
        if (e.name === 'AbortError')
          return;
        console.error('Price chart fetch error:', e);
        this.error = true;
      }
      this.loading = false;
    },

    async fetch12MonthData() {
      try {
        const now = new Date();
        const from = new Date();
        from.setFullYear(now.getFullYear() - 1);
        const url = buildApiUrl(this.apiUrl, toDateString(from), toDateString(now));
        const response = await fetch(url);
        if (!response.ok)
          throw new Error('API returned ' + response.status);

        const data = await response.json();
        if (isValidApiResponse(data))
          this.sidebarData = parseApiResponse(data);
      } catch (e) {
        console.error('Price chart 12-month fetch error:', e);
      }
    },

    setMetal(code) {
      var idx = this.selectedMetals.indexOf(code);
      if (idx === -1) {
        this.selectedMetals.push(code);
      } else if (this.selectedMetals.length > 1) {
        this.selectedMetals.splice(idx, 1);
      }
      this.$nextTick(this.resetSlider);
    },

    setUnit(key) {
      this.selectedUnit = key;
    },

    setCurrency(c) {
      this.selectedCurrency = c.toLowerCase();
      this.$nextTick(this.resetSlider);
    },

    setTimeSpan(key) {
      this.activeTimeSpan = key;
      const from = new Date();
      TIME_SPAN_OFFSETS[key](from);
      this.dateFrom = toDateString(from);
      this.dateTo = toDateString(new Date());
      this.fetchData();
    },

    onSliderInput(handle, rawValue) {
      const gap = 3;
      const max = this.sliderMaxIndex;
      let s = handle === 'start' ? rawValue : this.sliderStart;
      let e = handle === 'end' ? rawValue : this.sliderEnd;

      // Så slideren viser minimum 4 datapunkter på samme tid (gap 3).
      if (handle === 'start') {
        s = Math.min(s, max - gap);
        if (s > e - gap)
          e = Math.min(s + gap, max);
      }
      else {
        e = Math.max(e, gap);
        if (e < s + gap)
          s = Math.max(e - gap, 0);
      }

      // Force Vue to re-render inputs, to fix a slider button from getting stuck inside the other one.
      this.sliderStart = -1;
      this.sliderEnd = -1;
      this.$nextTick(() => {
        this.sliderStart = s;
        this.sliderEnd = e;
      });
    },

    resetSlider() {
      this.sliderStart = 0;
      this.sliderEnd = this.sliderMaxIndex;
    },

    formatPrice(value) {
      return formatNumber(value) + ' ' + this.unitSuffix;
    },

    formatPct(value) {
      return formatPercent(value);
    },

    // Mobile methods
    applyFilter(filterState) {
      this.selectedMetals = filterState.metals;
      this.selectedUnit = filterState.unit;
      this.selectedCurrency = filterState.currency;
      this.filterModalOpen = false;
      if (filterState.dateFrom !== this.dateFrom || filterState.dateTo !== this.dateTo) {
        this.dateFrom = filterState.dateFrom;
        this.dateTo = filterState.dateTo;
        this.fetchData();
      } else {
        this.$nextTick(this.resetSlider);
      }
    },

    aggregateByPeriod(points, keyFn) {
      const groups = {};
      const order = [];
      points.forEach(p => {
        const k = keyFn(p.rawDate);
        if (!groups[k]) order.push(k);
        groups[k] = p;
      });
      return order.map(k => groups[k]);
    },

    isoWeekNumber(d) {
      const tmp = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
      tmp.setUTCDate(tmp.getUTCDate() + 4 - (tmp.getUTCDay() || 7));
      const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
      return Math.ceil(((tmp - yearStart) / 86400000 + 1) / 7);
    },

    handleMediaChange(e) {
      this.isMobile = e.matches;
      if (!this.isMobile) {
        this.filterModalOpen = false;
      }
    }
  },

  mounted() {
    this.fetchData();
    this.fetch12MonthData();
    this.mobileMediaQuery = window.matchMedia('(max-width: 768px)');
    this.isMobile = this.mobileMediaQuery.matches;
    this.mobileMediaQuery.addEventListener('change', this.handleMediaChange);
  },

  beforeDestroy() {
    if (this.fetchController)
      this.fetchController.abort();
    if (this.mobileMediaQuery) {
      this.mobileMediaQuery.removeEventListener('change', this.handleMediaChange);
    }
  }
}
</script>

<style>
.ka-price-chart {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  background: #FAF8F4;
  border-radius: 12px;
  padding: 28px;
  color: #333;
  box-sizing: border-box;
  width: 90vw;
  max-width: 1600px;
  position: relative;
  left: 50%;
  transform: translateX(-50%);
}

.ka-price-chart *,
.ka-price-chart *::before,
.ka-price-chart *::after {
  box-sizing: border-box;
}

.ka-pc-title {
  font-size: 22px;
  font-weight: 600;
  margin: 0 0 20px 0;
  color: #222;
}

/* Filters */
.ka-pc-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid #E5DFD5;
}

.ka-pc-filter-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ka-pc-filter-label {
  font-size: 12px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.ka-pc-btn-group {
  display: flex;
  gap: 4px;
}

.ka-pc-btn {
  padding: 7px 14px !important;
  border: none !important;
  border-radius: 6px !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  cursor: pointer;
  transition: all 0.15s ease;
  background: #EDE8DC !important;
  color: #666 !important;
  margin: 0 !important;
  line-height: normal !important;
}

.ka-pc-btn:hover {
  background: #DDD6C4 !important;
}

.ka-pc-btn.active {
  background: #C9A84C !important;
  color: #fff !important;
}

.ka-pc-date-inputs {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ka-price-chart .ka-pc-date {
  padding: 6px 10px !important;
  border: 1px solid #DDD6C4 !important;
  border-radius: 6px !important;
  font-size: 13px !important;
  background: #fff !important;
  color: #333 !important;
  width: auto;
  max-width: 150px;
  height: auto;
  line-height: normal !important;
  margin: 0 !important;
}

.ka-pc-date-sep {
  color: #999;
}

/* Main layout */
.ka-pc-main {
  display: flex;
  gap: 24px;
}

.ka-pc-chart-col {
  flex: 1;
  min-width: 0;
}

.ka-pc-sidebar {
  width: 260px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Chart */
.ka-pc-chart-container {
  position: relative;
  background: #fff;
  border-radius: 10px;
  padding: 16px;
  min-height: 400px;
  border: 1px solid #E5DFD5;
}

.ka-pc-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(250, 248, 244, 0.85);
  border-radius: 10px;
  z-index: 10;
  font-size: 14px;
  color: #888;
}

.ka-pc-error {
  color: #c44;
}

.ka-pc-no-data {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 350px;
  color: #999;
  font-size: 14px;
}

/* Time spans */
.ka-pc-time-spans {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.ka-pc-ts-btn {
  flex: 1 1 0;
  min-width: 0;
  padding: 5px 0 !important;
  border: 1px solid #E5DFD5 !important;
  border-radius: 5px !important;
  font-size: 12px !important;
  font-weight: 500 !important;
  cursor: pointer;
  background: #fff !important;
  color: #777 !important;
  transition: all 0.15s ease;
  text-align: center;
  margin: 0 !important;
  line-height: normal !important;
  white-space: nowrap !important;
}

.ka-pc-ts-btn:hover {
  border-color: #C9A84C !important;
  color: #C9A84C !important;
}

.ka-pc-ts-btn.active {
  background: #C9A84C !important;
  border-color: #C9A84C !important;
  color: #fff !important;
}

/* Range slider */
.ka-pc-slider-area {
  flex: 0 0 100%;
  margin-top: 4px;
  text-align: center;
}

.ka-pc-slider-track {
  position: relative;
  height: 28px;
}

.ka-pc-range {
  position: absolute;
  left: 0;
  right: 0;
  width: 100%;
  top: 4px;
  height: 24px;
  pointer-events: none;
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
  margin: 0;
  padding: 0;
  z-index: 3;
}

/* Since there are two tracks, hide one to avoid overlap over button. (One track per button. One for max, one for min) */
.ka-pc-range-max::-webkit-slider-runnable-track {
  background: transparent !important;
}
.ka-pc-range-max::-moz-range-track {
  background: transparent !important;
}

.ka-pc-range::-webkit-slider-runnable-track {
  height: 4px;
  background: #E5DFD5;
  border-radius: 2px;
}

.ka-pc-range::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  pointer-events: all;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #C9A84C;
  border: 2px solid #fff;
  box-shadow: 0 1px 4px rgba(0,0,0,0.2);
  cursor: pointer;
  margin-top: -7px;
}

.ka-pc-range::-moz-range-thumb {
  pointer-events: all;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #C9A84C;
  border: 2px solid #fff;
  box-shadow: 0 1px 4px rgba(0,0,0,0.2);
  cursor: pointer;
}

.ka-pc-range::-moz-range-track {
  height: 4px;
  background: #E5DFD5;
  border-radius: 2px;
  border: none;
}

.ka-pc-slider-label {
  display: block;
  margin-top: 4px;
  font-size: 11px;
  color: #aaa;
}

/* Sidebar cards */
.ka-pc-card {
  background: #fff;
  border-radius: 10px;
  padding: 18px;
  border: 1px solid #E5DFD5;
}

.ka-pc-card-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin: 0 0 14px 0;
  padding-bottom: 10px;
  border-bottom: 1px solid #F0ECE4;
}

.ka-pc-stat {
  margin-bottom: 12px;
}

.ka-pc-stat:last-child {
  margin-bottom: 0;
}

.ka-pc-stat-label {
  font-size: 11px;
  color: #999;
  margin-bottom: 3px;
}

.ka-pc-stat-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ka-pc-stat-value {
  font-size: 15px;
  font-weight: 600;
  color: #333;
}

.ka-pc-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
}

.ka-pc-badge.positive {
  background: #ECFDF5;
  color: #16A34A;
}

.ka-pc-badge.negative {
  background: #FEF2F2;
  color: #DC2626;
}

/* ============================
   Mobile: Spot price bar
   ============================ */
.ka-pc-spot-bar {
  display: none;
}

.ka-pc-spot-item {
  padding: 10px 0;
  border-bottom: 1px solid #E5DFD5;
}

.ka-pc-spot-item:last-child {
  border-bottom: none;
}

.ka-pc-spot-name {
  font-size: 11px;
  color: #888;
  margin-bottom: 2px;
}

.ka-pc-spot-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.ka-pc-spot-price {
  font-size: 22px;
  font-weight: 700;
  color: #222;
}

.ka-pc-spot-unit {
  font-size: 14px;
  color: #888;
  font-weight: 500;
}

/* ============================
   Mobile: Filter header row
   ============================ */
.ka-pc-mobile-header {
  display: none;
}

.ka-pc-filter-btn {
  padding: 8px 20px !important;
  font-size: 14px !important;
}

/* ============================
   Mobile: Filter modal
   ============================ */
.ka-pc-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 9999;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.ka-pc-modal {
  background: #FAF8F4;
  border-radius: 16px 16px 0 0;
  padding: 28px 24px;
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
}

.ka-pc-modal-title {
  font-size: 20px;
  font-weight: 600;
  color: #222;
  margin: 0 0 20px 0;
}

.ka-pc-modal-section {
  margin-bottom: 20px;
}

.ka-pc-modal-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #555;
  margin-bottom: 10px;
}

.ka-pc-modal-check {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  cursor: pointer;
  font-size: 15px;
  color: #333;
}

.ka-pc-modal-check input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.ka-pc-check-box {
  width: 22px;
  height: 22px;
  border: 2px solid #ccc;
  border-radius: 5px;
  flex-shrink: 0;
  position: relative;
  transition: all 0.15s ease;
  background: #fff;
}

.ka-pc-check-box.checked {
  background: #C9A84C;
  border-color: #C9A84C;
}

.ka-pc-check-box.checked::after {
  content: '';
  position: absolute;
  left: 6px;
  top: 2px;
  width: 6px;
  height: 11px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.ka-pc-check-text {
  flex: 1;
}

.ka-pc-modal .ka-pc-date {
  flex: 1;
  min-width: 0;
  max-width: none;
  font-size: 15px !important;
  padding: 10px !important;
}

.ka-pc-modal-actions {
  display: flex;
  gap: 10px;
  margin-top: 24px;
}

.ka-pc-modal-btn {
  flex: 1;
  padding: 12px !important;
  border: none !important;
  border-radius: 8px !important;
  font-size: 15px !important;
  font-weight: 600 !important;
  cursor: pointer;
  font-family: inherit;
  margin: 0 !important;
  line-height: normal !important;
}

.ka-pc-modal-btn--back {
  background: #EDE8DC !important;
  color: #666 !important;
}

.ka-pc-modal-btn--save {
  background: #C9A84C !important;
  color: #fff !important;
}

/* ============================
   Mobile responsive layout
   ============================ */
@media (max-width: 768px) {
  .ka-price-chart {
    width: 100%;
    max-width: 100%;
    left: 0;
    transform: none;
    padding: 16px;
    border-radius: 8px;
  }

  .ka-pc-title {
    font-size: 16px;
    margin-bottom: 12px;
  }

  .ka-pc-spot-bar {
    display: block;
    margin-bottom: 12px;
    background: #fff;
    border: 1px solid #E5DFD5;
    border-radius: 10px;
    padding: 12px 18px;
  }

  .ka-pc-mobile-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid #E5DFD5;
  }

  .ka-pc-main {
    flex-direction: column;
  }

  .ka-pc-sidebar {
    width: 100%;
    flex-direction: row;
    gap: 12px;
  }

  .ka-pc-chart-container {
    min-height: 260px;
    padding: 10px;
  }

  .ka-pc-no-data {
    min-height: 220px;
  }

  .ka-pc-time-spans {
    flex-wrap: nowrap !important;
    gap: 4px;
    margin-top: 8px;
  }

  .ka-pc-ts-btn {
    padding: 6px 0 !important;
    font-size: 11px !important;
    border-radius: 16px !important;
    white-space: nowrap !important;
  }

  .ka-pc-card {
    margin-top: 12px;
  }

  .ka-pc-card-title {
    font-size: 13px;
    margin-bottom: 10px;
    padding-bottom: 8px;
  }

  .ka-pc-stat-label {
    font-size: 10px;
  }

  .ka-pc-stat-value {
    font-size: 13px;
  }

  .ka-pc-badge {
    font-size: 10px;
    padding: 2px 6px;
  }

  .ka-pc-spot-name {
    font-size: 10px;
  }

  .ka-pc-spot-price {
    font-size: 18px;
  }

  .ka-pc-spot-unit {
    font-size: 12px;
  }

  .ka-pc-slider-area {
    display: none;
  }
}

</style>
